import { SectionHeader } from '../common/section-header'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DisclosureChevron, Badge, Button, Icon, Notice, Tooltip } from '@speaking-coach/ui'
import type { ComparisonCefrLevel } from '@speaking-coach/shared-types'
import { ContentSelect } from './content-scope'
import { tutorLevelAccess } from './catalog-types'

type TutorPromptType = 'context' | 'summary' | 'grammar_feedback' | 'general_feedback' | 'mission' | 'hint' | 'coach_behavior'
export interface TutorPromptDetail {
  promptType: TutorPromptType
  difficulty: ComparisonCefrLevel | null
  promptBody: string
}
const levels = ['A1', 'A2', 'B1', 'B2', 'C1']
const labels: Record<TutorPromptType, string> = {
  context: 'Conversation context', summary: 'Call summary', grammar_feedback: 'Grammar feedback',
  general_feedback: 'General feedback', mission: 'Mission', hint: 'Hint', coach_behavior: 'Coach behavior',
}
const promptKey = (prompt: TutorPromptDetail) => `${prompt.promptType}:${prompt.difficulty ?? 'all'}`

export function TutorPrompts({ prompts, minimumCefrLevel, maximumCefrLevel }: {
  prompts: TutorPromptDetail[]
  minimumCefrLevel: string | null
  maximumCefrLevel: string | null
}) {
  const [params, setParams] = useSearchParams()
  const requestedLevel = params.get('promptLevel') ?? ''
  const level = levels.includes(requestedLevel) ? requestedLevel : ''
  const [open, setOpen] = useState<Set<string>>(() => new Set())
  // Match ResolvedTutorRevision.prompt_body / context_prompt_difficulties:
  // A1 → A2 → B1; every other missing context → B1. Other types use null.
  const candidates = level === 'A1' ? ['A1', 'A2', 'B1'] : [...new Set([level, 'B1'])]
  const context = candidates.map(candidate => prompts.filter(prompt => prompt.promptType === 'context' && prompt.difficulty === candidate))
    .find(matches => matches.length === 1)?.[0]
  const visible = level ? prompts.filter(prompt => prompt === context || (prompt.promptType !== 'context' && prompt.difficulty === null)) : prompts
  const fallback = level && context && context.difficulty !== level
  const rank = levels.indexOf(level)
  const minimumRank = levels.indexOf(minimumCefrLevel?.replace(/\+$/, '') ?? '')
  const maximumRank = levels.indexOf(maximumCefrLevel?.replace(/\+$/, '') ?? '')
  const outsideRange = level && ((minimumRank >= 0 && rank < minimumRank) || (maximumRank >= 0 && rank > maximumRank))
  const allOpen = visible.length > 0 && visible.every(prompt => open.has(promptKey(prompt)))
  const anyOpen = visible.some(prompt => open.has(promptKey(prompt)))
  function setVisibleOpen(expanded: boolean) {
    setOpen(previous => {
      const next = new Set(previous)
      for (const prompt of visible) {
        if (expanded) next.add(promptKey(prompt))
        else next.delete(promptKey(prompt))
      }
      return next
    })
  }
  return <section className="space-y-4 rounded-token-card border border-border-default bg-surface-elev p-5" aria-labelledby="tutor-prompts-heading">
    <SectionHeader
      id="tutor-prompts-heading"
      title="Tutor prompts"
      meta={<Badge variant="neutral">{level ? `${visible.length} of ${prompts.length}` : prompts.length} definitions</Badge>}
    />
    <p className="text-body text-text-secondary">Choose a level to see its saved context and shared definitions. Runtime substitutions and lesson instructions are added during calls.</p>
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="w-full sm:w-48"><ContentSelect label="Learner level" value={level} emptyLabel="All levels"
        options={levels.map(value => ({ value, label: value }))}
        onChange={value => {
          const next = new URLSearchParams(params)
          if (value) next.set('promptLevel', value)
          else next.delete('promptLevel')
          setParams(next, { replace: true })
        }} /></div>
      <div role="group" aria-label="Expand and collapse definitions" className="isolate inline-flex -space-x-px">
        <Tooltip label="Expand all"><Button variant="secondary" size="icon" aria-label="Expand all" className="relative rounded-r-none focus-visible:z-10" disabled={!visible.length || allOpen} onClick={() => setVisibleOpen(true)}><Icon name="expandAll" /></Button></Tooltip>
        <Tooltip label="Collapse all"><Button variant="secondary" size="icon" aria-label="Collapse all" className="relative rounded-l-none focus-visible:z-10" disabled={!anyOpen} onClick={() => setVisibleOpen(false)}><Icon name="collapseAll" /></Button></Tooltip>
      </div>
    </div>
    {outsideRange ? <Notice variant="neutral">{level} is outside this Tutor’s learner-level range ({tutorLevelAccess(minimumCefrLevel, maximumCefrLevel)}). Definitions are shown for inspection.</Notice> : null}
    {level && !context ? <Notice variant="neutral">No conversation context resolves for {level}. The requested definition and its fallbacks are missing.</Notice> : null}
    {fallback ? <p className="text-body text-text-secondary">{level} uses the {context.difficulty} conversation context as a fallback.</p> : null}
    {visible.length ? <div className="space-y-2">
      {visible.map(prompt => {
        const key = promptKey(prompt)
        const label = labels[prompt.promptType]
        const scope = prompt.difficulty ?? 'All levels'
        return <details key={key} open={open.has(key)} className="group overflow-hidden rounded-token-card border border-border-default bg-surface-app" aria-label={`${label} prompt · ${scope}`}>
          <summary onClick={event => {
            event.preventDefault()
            setOpen(previous => {
              const next = new Set(previous)
              if (next.has(key)) next.delete(key)
              else next.add(key)
              return next
            })
          }} className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 marker:content-none">
            <span className="flex min-w-0 flex-wrap items-center gap-2"><span className="font-semibold text-text-primary">{label}</span><Badge variant="muted">{scope}</Badge>{fallback && prompt === context ? <Badge variant="neutral">Fallback</Badge> : null}</span>
            <DisclosureChevron className="text-text-secondary" />
          </summary>
          <div className="px-4 pb-4"><pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap font-mono text-caption text-text-primary"><code>{prompt.promptBody}</code></pre></div>
        </details>
      })}
    </div> : <Notice variant="neutral">{prompts.length ? 'No prompt definitions apply to this selection.' : 'No prompt definitions are recorded for this Tutor.'}</Notice>}
  </section>
}
